import styled from 'styled-components';

import {
  Color,
  Typography,
  FlexContainer,
  Width,
  FakeLink,
  Legal as BaseLegal,
  Asterisk,
} from '@figure/frontlib-gui';
import { Header as HeaderBase } from '@figure/figure-frontlib-gui';
import { useRedirect, useTelemetry, useLeaveSurveyModal, useIdentity } from 'shared/hooks';
import { CSDecline } from 'shared/constants';
import cellphones from 'shared/assets/images/2cellphones.svg';
import checkmark from 'shared/assets/images/checkmark-black.svg';
import AppleStore from 'shared/assets/images/apple-store-dark.svg';
import PlayStore from 'shared/assets/images/play-store-dark.svg';

const PurpleBox = styled.div`
  background-color: #f0eefc;
  padding: 2rem 2rem 0 2rem;
`;

const Legal = styled(BaseLegal)`
  margin: 12px 0;
`;

const PurpleBody = styled(Typography.SmallBody)`
  padding-bottom: 1rem;
  color: #573ae6;
`;

const HdrBase = styled(Typography.H2)`
  @media print, screen and (max-width: ${Width.MD}px) {
    font-size: 2.2rem;
  }
`;

const HdrTop = styled(HdrBase)`
  border-bottom: solid 1px #e2ddf8;
  padding: 3rem;
`;

const HdrBot = styled(HdrBase)``;

const SmallOnly = styled.div`
  @media print, screen and (min-width: ${Width.MD}px) {
    display: none;
    float: left;
    clear: both;
    position: absolute;
  }
`;

const FContainer = styled(FlexContainer)`
  flex-direction: row;
  align-items: center;
  gap: 70px;
  border-bottom: solid 1px #e2ddf8;
  margin-bottom: 30px;
  padding: 14px 0;

  @media print, screen and (max-width: ${Width.MD}px) {
    flex-direction: column-reverse;
    gap: 30px;
  }
`;

const Img = styled.img`
  width: 300px;
  top: 10px;
  right: 10px;

  @media print, screen and (max-width: ${Width.MD}px) {
    margin-left: auto;
    margin-right: auto;
    width: 70%;
  }
`;

const FLink = styled(FakeLink)`
  color: inherit;
  padding-right: 0;
`;

const UL = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
`;

const LI = styled.div`
  display: flex;

  font-size: 2.2rem;
  &::before {
    content: url(${checkmark});
    padding-right: 10px;
  }
`;

const QualifyDiv = styled.div`
  padding-bottom: 20px;
  padding-top: 20px;
`;

const AppStoreLink = styled.a`
  display: inline-flex;

  &:last-of-type {
    margin-left: 14px;
  }

  &:focus {
    border-radius: 2px;
    box-shadow: 0 0 0 4px ${Color.FOCUS_B};
    background-color: ${Color.FOCUS_B};
    outline: none;
  }
`;

const BaseContainer = styled.div`
  padding: 2rem;
  height: 100%;
  background-color: #f5f7fd;
`;

const Content = styled.div`
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;
`;

// super kludgy to get the background right until this is fixed in figure-fe-gui
const Header = styled(HeaderBase)`
  & > * > * > ul > li {
    background-color: white;
  }
`;

const FPayCSWrapper = styled.div`
  padding: 2rem;
  background-color: ${Color.WHITE};

  @media print, screen and (min-width: ${Width.SM}px) {
    padding: 3.2rem 4rem;
  }
`;

const SmallHeader = styled(Typography.H6)`
  margin-bottom: 18px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 900;
  font-size: 18px;
  line-height: 24px;
  white-space: nowrap;
  &::before {
    content: 'Meet Figure Pay, the all-in-one money app:';
    @media print, screen and (max-width: ${Width.MD}px) {
      content: 'Introducing Figure Pay:';
    }
  }
`;

const MenuItem = styled.span`
  cursor: pointer;
  display: block;
  padding: 10px 15px;
`;

const Itext = styled.span`
  font-weight: 700;
`;

const LittleHeader = styled(Typography.H5)`
  padding-bottom: 20px;
`;

const DeclineWithFPayCrossSellContainer = () => {
  const { redirectToDashboard } = useRedirect();
  const { telemetry } = useTelemetry();
  const { resetSurvey } = useLeaveSurveyModal();
  const { logout } = useIdentity();

  const handleDashboardClick = () => {
    redirectToDashboard();
  };

  const handleLogout = () => {
    logout();
    window.location.reload();
  };

  const appStoreLinks = [
    {
      url: CSDecline.FIGUREPAY_APPLESTORE,
      icon: AppleStore,
      alt: 'Apple Store',
      tag: 'AppleStore',
    },
    {
      url: CSDecline.FIGUREPAY_PLAYSTORE,
      icon: PlayStore,
      alt: 'Play Store',
      tag: 'PlayStore',
    },
  ];

  const StaticBullets = () => (
    <div>
      <SmallHeader />

      <UL>
        {[
          <>Sign up in less than 2 minutes</>,
          <>
            As you use Figure Pay to access and pay loans off on time, you could{' '}
            <Itext>build your credit history</Itext> and potentially unlock larger loan amounts in
            the future with Figure*
          </>,
          <>
            Easily pay for purchases and automatically <Itext>earn cash back¹</Itext> at hundreds of
            retailers (up to 10%!)
          </>,
          <>
            Enjoy <Itext>no fees</Itext> and <Itext>no minimum balance</Itext> requirement on your
            deposit account
          </>,
        ].map((b) => (
          <LI>
            <Typography.Body>{b}</Typography.Body>
          </LI>
        ))}
      </UL>
      {SocialMediaSection()}
    </div>
  );

  const StaticQualify = () => (
    <>
      <HdrBot>It’s easy to qualify for a Figure Pay loan:</HdrBot>
      <QualifyDiv>
        <LittleHeader>Just set up up direct deposit</LittleHeader>
        <Typography.Body>
          Provide your employer with your Figure Pay routing and account numbers (you can find this
          in the app). Once a minimum of $1200/month for two consecutive months has been deposited,
          simply apply for the loan on the app and you’ll be guaranteed approval for a Buy Now, Pay
          Later loan with no impact to your credit score. You--ll also then be eligible to{' '}
          <Itext>get a $100 bonus</Itext> within 30 days!²
        </Typography.Body>
      </QualifyDiv>
      <SmallOnly>{SocialMediaSection()}</SmallOnly>
    </>
  );

  const SocialMediaSection = () => {
    const refAppStoreLink = (storeLink) => {
      telemetry(`pl${storeLink.tag}Redirect`);
      window.open(storeLink.url);
    };

    return (
      <FlexContainer flexDirection="row">
        {appStoreLinks.map((storeLink) => (
          <AppStoreLink onClick={() => refAppStoreLink(storeLink)}>
            <img src={storeLink.icon} alt={storeLink.alt} />
          </AppStoreLink>
        ))}
      </FlexContainer>
    );
  };

  const statesList =
    CSDecline.FigurePayStates.map((s) => s.name)
      .slice(0, -1)
      .join(', ') +
    ' and ' +
    CSDecline.FigurePayStates[CSDecline.FigurePayStates.length - 1].name;

  return (
    <BaseContainer>
      <Header
        menuList={[
          <MenuItem
            onClick={() => {
              // don't show exit survey
              resetSurvey();
              redirectToDashboard();
            }}
          >
            Dashboard
          </MenuItem>,
          <MenuItem onClick={handleLogout}>Sign out</MenuItem>,
        ]}
      />
      <Content>
        <FPayCSWrapper>
          <PurpleBox>
            <PurpleBody>
              Unfortunately, we cannot offer you a Figure Personal Loan at this time.
            </PurpleBody>
            <PurpleBody>
              You will find an explanation of our decision in your adverse action notice, which will
              be delivered to you via email and added to your
              <FLink onClick={handleDashboardClick}>dashboard</FLink>
              {'.'}
            </PurpleBody>
          </PurpleBox>
          <HdrTop>
            Get access to larger loans as you borrow and build your credit history with Figure Pay
          </HdrTop>
          <FContainer>
            <Img alt="" src={cellphones} />
            <div>{StaticBullets()}</div>
          </FContainer>
          {StaticQualify()}
        </FPayCSWrapper>

        <Legal>
          The Figure Pay Loan is offered by Figure Lending LLC dba Figure. 15720 Brixham Hill Ave,
          Suite 300 Charlotte, NC 28277 (888) 819-6388 NMLS #1717824{' '}
          <a href="http://www.nmlsconsumeraccess.org" target="_blank" rel="noopener noreferrer">
            www.nmlsconsumeraccess.org
          </a>
          . Equal Opportunity Lender. Standard data charges may apply. Terms apply.
        </Legal>
        <Legal>
          The Figure Pay-branded demand deposit account is held at UMB Bank, n.a., member FDIC, with
          program management services provided by Figure Payments Corporation. Figure Payments
          Corporation dba Figure Pay | NMLS #2033432. 100 West Liberty Street, Suite 600, Reno, NV
          89501. (800) 260-1795.
        </Legal>

        <Legal>
          <Asterisk />
          Figure Lending LLC will report the status of your Figure Pay loans to the consumer
          reporting agencies ("CRAs"). Each CRA generates a credit score based on its own criteria,
          which may or may not include information we report about your Figure Pay loan.
        </Legal>

        <Legal>
          Figure Pay Buy Now Pay Later loans provide you access with a closed-end line of credit
          between $75 and $2,500 that you can use to make an in-person purchase.
        </Legal>
        <Legal>
          Any third parties listed are not affiliated with Figure Technologies. and are solely
          responsible for their products and services. All trademarks are the property of their
          respective owners.
        </Legal>

        <Legal>
          Figure Pay is available in [{statesList}] with more states to come. Figure Pay will expand
          its availability based on regulatory approval on a state-by-state basis. For any questions
          regarding Figure Pay, including availability, please call Figure Pay customer support at
          1-800-260-1795 or visit figurepay.com
        </Legal>

        <Legal>
          1 Figure Pay Rewards are managed by Dosh. Dosh is a third party that is not affiliated
          with MetaBank, National Association, or Visa.
        </Legal>

        <Legal>
          2 Limit one $100 bonus per customer. Bonus will be deposited in the Figure Pay account
          within 30 days of the second consecutive deposit satisfying requirements for bonus.
        </Legal>
      </Content>
    </BaseContainer>
  );
};

export default DeclineWithFPayCrossSellContainer;
